import React from "react";
import { Flame, MapPin } from "lucide-react";

const VARIANTS = [
  {
    id: "bbq_boat_ad_v2",
    bg: "/menu/bbq_boat_v2.png",
    headline: (
      <>
        Two buns.
        <br />
        One <span style={{ color: "#FF3B30" }}>mission</span>.
      </>
    ),
    align: "bottom",
  },
  {
    id: "bbq_boat_ad_v3",
    bg: "/menu/bbq_boat_v3.png",
    headline: (
      <>
        Hot dog buns.
        <br />
        <span style={{ color: "#FF3B30" }}>No hot dogs.</span>
      </>
    ),
    align: "bottom",
  },
];

function BoatAd({ variant }) {
  return (
    <div
      data-testid={`boat-ad-${variant.id}`}
      className="relative w-[540px] h-[540px] overflow-hidden bg-black"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${variant.bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(1.18) contrast(1.05)",
        }}
      />
      {/* Dark gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 32%, rgba(0,0,0,0.4) 65%, rgba(0,0,0,0.96) 100%)",
        }}
      />
      {/* Ember glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(255,59,48,0.32), transparent 60%)",
        }}
      />
      {/* Grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Brand top-left */}
      <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
        <Flame
          className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.7)]"
          style={{ width: 18, height: 18 }}
        />
        <span
          className="font-['Anton'] uppercase text-white tracking-[0.3em]"
          style={{ fontSize: 13 }}
        >
          Smokey <span className="text-[#FF3B30]">AF</span>
        </span>
      </div>

      {/* City of Fremont badge top-right */}
      <div
        className="absolute top-5 right-5 bg-[#FF3B30] text-white font-['Anton'] uppercase tracking-wider px-3 py-1.5 rotate-3 shadow-[0_8px_24px_rgba(255,59,48,0.5)]"
        style={{ fontSize: 13 }}
      >
        City of Fremont
      </div>

      {/* Eyebrow */}
      <div className="absolute top-[60px] left-6 right-6 z-10">
        <div className="flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-[#FF8C00]" />
          <span
            className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.45em]"
            style={{ fontSize: 10 }}
          >
            Fan Favorite · The BBQ Boat
          </span>
        </div>
      </div>

      {/* Big headline */}
      <div className="absolute top-[100px] left-6 right-6 z-10">
        <h1
          className="font-['Anton'] uppercase text-white leading-[0.82] drop-shadow-[0_8px_25px_rgba(0,0,0,0.95)]"
          style={{ fontSize: 60, letterSpacing: "-0.02em" }}
        >
          {variant.headline}
        </h1>
      </div>

      {/* Bottom — price + CTA */}
      <div className="absolute inset-x-0 bottom-0 px-6 pb-6 z-10">
        <div className="flex items-end justify-between gap-3">
          <div>
            <span
              className="font-['Outfit'] uppercase tracking-[0.35em] text-[#FF8C00]"
              style={{ fontSize: 10 }}
            >
              The BBQ Boat
            </span>
            <p
              className="font-['Outfit'] italic text-zinc-200 mt-1"
              style={{ fontSize: 13 }}
            >
              Toasted buns. Pulled pork. Chipotle.
              <br />
              Coleslaw on top.
            </p>
          </div>
          <div
            className="bg-[#FF3B30] text-white font-['Anton'] tracking-wider px-3 py-2 rotate-3 shadow-[0_8px_24px_rgba(255,59,48,0.5)] flex-shrink-0"
            style={{ fontSize: 34 }}
          >
            $14
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span
            className="font-['Anton'] tracking-widest uppercase bg-white text-black px-3 py-1.5"
            style={{ fontSize: 11 }}
          >
            Order Online
          </span>
          <span
            className="font-['Outfit'] uppercase text-zinc-400 tracking-[0.3em]"
            style={{ fontSize: 9 }}
          >
            smokey-af-bbq.emergent.host
          </span>
        </div>
      </div>
    </div>
  );
}

export default function BBQBoatAds() {
  return (
    <div className="min-h-screen bg-zinc-800 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-['Anton'] text-white text-3xl tracking-widest uppercase mb-2">
          BBQ Boat Ads · 1080×1080
        </h1>
        <p className="font-['Outfit'] text-zinc-400 mb-8 text-sm">
          Two variants. Pick whichever hits hardest.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {VARIANTS.map((v) => (
            <div key={v.id} className="flex flex-col items-center gap-3">
              <BoatAd variant={v} />
              <a
                href={`/ads/${v.id}.png`}
                download
                data-testid={`boat-dl-${v.id}`}
                className="font-['Anton'] text-xs tracking-widest uppercase bg-[#FF3B30] hover:bg-white hover:text-black text-white px-5 py-2.5 transition-colors"
              >
                Download {v.id.replace("bbq_boat_ad_", "Variant ")}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
